import React from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import SecondaryButton from "../components/Buttons/SecondaryButton";

export interface OrganizationToDelete {
  id: string;
  name: string;
}

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  deletePassword: string;
  setDeletePassword: (val: string) => void;
  deleteError: string;
  deleteLoading: boolean;
  organizationsToDelete: OrganizationToDelete[];
  loadingOrganizations: boolean;
  isPsdiUser?: boolean;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  onClose,
  onSubmit,
  deletePassword,
  setDeletePassword,
  deleteError,
  deleteLoading,
  organizationsToDelete,
  loadingOrganizations,
  isPsdiUser = false,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-4">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md mt-24">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h5 className="text-base font-semibold tracking-tight text-red-600">Delete account</h5>
          <button
            type="button"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            onClick={onClose}
            disabled={deleteLoading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="px-5 py-4 space-y-3 text-sm text-gray-600">
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <p>This will permanently delete:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Your profile and account data</li>
              <li>Your personal documents and pages</li>
              <li>All associated data from our systems</li>
            </ul>
            <p className="text-xs text-gray-500">
              <strong className="text-gray-700">Note:</strong> Documents associated with organisations
              will be preserved and remain accessible to organisation members.
            </p>

            {loadingOrganizations && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                Loading organisations...
              </div>
            )}

            {!loadingOrganizations && organizationsToDelete.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                <p className="font-semibold mb-1 text-sm">Cannot delete account</p>
                <p className="text-xs mb-1">
                  You are the only admin of the following organisation(s):
                </p>
                <ul className="list-disc list-inside text-xs mb-1">
                  {organizationsToDelete.map((org) => (
                    <li key={org.id}>{org.name}</li>
                  ))}
                </ul>
                <p className="text-xs">Please contact us if you want to delete your account.</p>
              </div>
            )}

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-yellow-800 text-xs">
              Some data may stay in backups or logs as part of our data retention policy for legal
              and security reasons. You can read the full details in our{" "}
              <Link to="/legal/privacy-policy" className="text-[#1d365d] hover:text-[#2d4a75] transition-colors">
                Privacy Policy
              </Link>.
            </div>

            {!isPsdiUser && (
              <div>
                <label htmlFor="deletePassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                  id="deletePassword"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={deleteLoading}
                />
              </div>
            )}

            {deleteError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {deleteError}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
            <SecondaryButton type="button" onClick={onClose} disabled={deleteLoading}>
              Cancel
            </SecondaryButton>
            <button
              type="submit"
              className="h-9 px-4 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={deleteLoading || organizationsToDelete.length > 0}
            >
              {deleteLoading ? "Deleting..." : "Delete account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
